import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions, MUIDataTableState } from 'mui-datatables';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { GenericMessages } from '../../intl/GenericMessages';
import { ProfileMessages } from '../../intl/ProfileMessages';
import { ListOrganizationsRequest, Organization, SentinelFeatureFlag } from '../../models/organization';
import { DqLoading } from '../DqLoading';
import { SearchResponse } from '../../utils/types';
import { debounce } from 'lodash';
import { Chip, ChipType } from '../reusable-components/Chip';
import SentinelLogo from '../../assets/images/SentinelLogo.png';
import { useSocketList } from '../../utils/socket';
import { SocketContextSentinel } from '../../utils/socket/context/sentinelSocketContext';

export interface OrganizationSubscriptionsTableProps {
  loading: boolean;
  data?: SearchResponse<Organization>;
  request: ListOrganizationsRequest;
  onTableChange: (request: Partial<ListOrganizationsRequest>) => void;
}

interface OrganizationSubscriptionsTableItem {
  _id: string;
  orgName: string;
  customerNumber?: string;
  liveStreamInvoiceNumber: string;
  liveStreamChip: { display: string; chipType: ChipType };
  buildABundleInvoiceNumber: string;
  bundleChip: { display: string; chipType: ChipType };
}

const getFeatureFlagStateChipText = (
  formatMessage: any,
  formatDate: any,
  feature?: SentinelFeatureFlag,
): { display: string; chipType: ChipType } => {
  if (feature == null) {
    return { display: formatMessage(GenericMessages.notApplicableLabel), chipType: ChipType.ORG_FEATURE_NA };
  }

  const parsedEndDate = new Date(feature.endDate);
  const now = new Date();
  const expiredOrExpiresMessage = parsedEndDate <= now ? GenericMessages.expiredLabel : GenericMessages.expiresLabel;
  return {
    display: `${formatMessage(expiredOrExpiresMessage)} - ${formatDate(parsedEndDate)}`,
    chipType: parsedEndDate <= now ? ChipType.ORG_FEATURE_EXPIRED : ChipType.ORG_FEATURE_ACTIVE,
  };
};

export const OrganizationSubscriptionsTable: React.FC<OrganizationSubscriptionsTableProps> = ({
  loading,
  data,
  request,
  onTableChange,
}) => {
  const { formatMessage, formatDate } = useIntl();

  /**
   * Debounce a function for updating the search term query, we don't want
   * to fire off a request every key-press
   */
  const setSearchTerm = useMemo(() => {
    return debounce(
      (searchTerm: string | undefined) => {
        onTableChange({
          searchTerm: searchTerm,
          pageNumber: 0,
        });
      },
      250,
      { maxWait: 1000 },
    );
  }, [onTableChange]);

  // Get the list of all controllers and the boolean that indicates whether they are still loading.
  const ctrlQuery = React.useMemo(() => {
    return { options: { projection: { org: 1 } } };
  }, []);
  const { items: controllers, isLoading } = useSocketList(SocketContextSentinel, 'controllers', ctrlQuery);

  /**
   * Creates an object with org id as key and number of controllers as value.
   *
   * It will only contain orgs that have at least one associated controller.  If an org is not present, then its
   * controller count is zero.
   */
  const controllerCountsByOrg = useMemo(() => {
    if (isLoading) return {};

    const controllerCountsByOrg: Record<string, number> = {};
    for (const controller of controllers) {
      // If the org is present, increment its count by one.
      // Otherwise, this is the first encounter with the org so set its count to one.
      controllerCountsByOrg[controller.org] =
        controller.org in controllerCountsByOrg ? controllerCountsByOrg[controller.org] + 1 : 1;
    }

    return controllerCountsByOrg;
  }, [controllers, isLoading]);

  /**
   * Returns the number of controllers associated with the given organization.
   */
  const getControllerCount = useCallback(
    (orgId: string) => {
      // Return something other than zero so user is not misled/confused.
      if (isLoading) return '-';

      // If org is not present, then controller count is zero.
      return controllerCountsByOrg[orgId] ?? 0;
    },
    [controllerCountsByOrg, isLoading],
  );

  const tableOrganizations: OrganizationSubscriptionsTableItem[] | undefined = React.useMemo(() => {
    return data?.results?.map(org => {
      const liveStreamFeature = org.features?.find(f => f.feature === 'LOGS_LIVE_STREAM');
      const babFeature = org.features?.find(f => f.feature === 'BUNDLES');

      const liveStreamChip = getFeatureFlagStateChipText(formatMessage, formatDate, liveStreamFeature);
      const babStreamChip = getFeatureFlagStateChipText(formatMessage, formatDate, babFeature);

      return {
        _id: org._id,
        orgName: org.orgName,
        controllerCount: getControllerCount(org._id),
        customerNumber: org.customerNumber,
        liveStreamInvoiceNumber: liveStreamFeature?.changes?.find(f => !!f)?.invoiceNumber ?? '-',
        liveStreamChip: liveStreamChip,
        buildABundleInvoiceNumber: babFeature?.changes?.find(f => !!f)?.invoiceNumber ?? '-',
        bundleChip: babStreamChip,
      };
    });
  }, [getControllerCount, data?.results, formatDate, formatMessage]);

  const columns: MUIDataTableColumnDef[] = [
    {
      label: formatMessage(GenericMessages.idColumnLabel),
      name: '_id', // must be column 0! That way it is data[0] on rowClick. Thanks.
      options: {
        display: false,
        filter: false,
      },
    },
    {
      label: formatMessage(GenericMessages.organizationColumnLabel),
      name: 'orgName',
      options: {
        filter: false,
      },
    },
    {
      label: formatMessage(GenericMessages.organizationControllersLabel),
      name: 'controllerCount',
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      label: formatMessage(GenericMessages.customerNumberLabel),
      name: 'customerNumber',
      options: {
        filter: false,
      },
    },
    {
      label: formatMessage(GenericMessages.liveStreamLabel),
      name: 'liveStreamChip',
      options: {
        customBodyRender: (value: { display: string; chipType: ChipType }) => {
          return <Chip title={value.display} type={value.chipType} />;
        },
        filter: false,
        sort: false,
      },
    },
    {
      label: formatMessage(GenericMessages.invoiceNumberLabel),
      name: 'liveStreamInvoiceNumber',
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      label: formatMessage(GenericMessages.buildABundleLabel),
      name: 'bundleChip',
      options: {
        customBodyRender: (value: { display: string; chipType: ChipType }) => {
          return <Chip title={value.display} type={value.chipType} />;
        },
        filter: false,
        sort: false,
      },
    },
    {
      label: formatMessage(GenericMessages.invoiceNumberLabel),
      name: 'buildABundleInvoiceNumber',
      options: {
        filter: false,
        sort: false,
      },
    },
  ];

  const handleTableChange = (eventType: string, tableState: MUIDataTableState) => {
    switch (eventType) {
      case 'sort':
        onTableChange({
          sort: { property: tableState.sortOrder.name as keyof Organization, order: tableState.sortOrder.direction },
          pageNumber: tableState.page,
          pageSize: tableState.rowsPerPage,
        });
        break;
      case 'changePage':
        onTableChange({
          pageNumber: tableState.page,
          pageSize: tableState.rowsPerPage,
        });
        break;
      case 'changeRowsPerPage':
        onTableChange({
          pageNumber: tableState.page,
          pageSize: tableState.rowsPerPage,
        });
        break;
      case 'search':
        setSearchTerm(tableState.searchText ?? undefined);
        break;
      // These cases are not supported for this grid.
      case 'viewColumnsChange':
      case 'filterChange':
      default:
        break;
    }
  };

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    onTableChange: (eventType: string, tableState: MUIDataTableState) => handleTableChange(eventType, tableState),
    rowsPerPageOptions: [10, 25, 50],
    print: false,
    download: false,
    serverSide: true,
    count: data?.totalCount,
    filter: false,
    fixedSelectColumn: true,
    viewColumns: false,
    textLabels: {
      pagination: {
        next: formatMessage(GenericMessages.dataTablesNextPage),
        previous: formatMessage(GenericMessages.dataTablesPreviousPage),
        rowsPerPage: formatMessage(GenericMessages.dataTablesRowsPerPage),
        displayRows: formatMessage(GenericMessages.of),
      },
      body: {
        noMatch: loading ? (
          <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <DqLoading width="100px" img={SentinelLogo} />
          </div>
        ) : (
          <FormattedMessage {...ProfileMessages.stoneAgeUsersGridEmptyLabel} />
        ),
      },
    },
  } as MUIDataTableOptions;

  return <MUIDataTable columns={columns} data={tableOrganizations ?? []} options={options} title="" />;
};
